import { DatePipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { ConfirmModalComponent } from '@app/components/shared/confirm-modal/confirm-modal.component';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { RolesDialogComponent } from '@app/pages/home-page/shop-settings/settings-roles/roles-dialog/roles-dialog.component';
import { CONFIRM_DIALOG_OPTIONS } from '@app/utils/constants/confirm.constants';
import { ORDERS_HISTORY_COLUMNS_CONFIG } from '@app/utils/constants/orders.constants';
import { IconName } from '@app/utils/enums/icon-enum';
import {
  OrdersHistoryEnum,
  OrderStatus,
  OrderStatusApiValue,
} from '@app/utils/enums/orders-history.enum';
import { TranslatePipe } from '@ngx-translate/core';
import { commentValidator } from '../../validators/comment.validator';
import { AvailableStatus, Order, OrderHistoryItem } from '@app/utils/interfaces/order.interface';
import { OrdersService } from '../../services/orders.service';
import { catchError, EMPTY, finalize, tap } from 'rxjs';
import { MatSortModule } from '@angular/material/sort';
import { ErrorModalComponent } from '@app/components/shared/error-modal/error-modal.component';

@Component({
  selector: 'app-edit-status-dialog',
  standalone: true,
  imports: [
    TranslatePipe,
    MatProgressSpinnerModule,
    IconComponent,
    ReactiveFormsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatTableModule,
    MatInputModule,
    NgClass,
    DatePipe,
    MatSortModule,
  ],
  templateUrl: './edit-status-dialog.component.html',
  styleUrl: './edit-status-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditStatusDialogComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogRef = inject(MatDialogRef<RolesDialogComponent>);
  private readonly dialog = inject(MatDialog);
  private readonly fb = inject(FormBuilder);
  private readonly ordersService = inject(OrdersService);

  public readonly orderItem = inject<{ order: Order }>(MAT_DIALOG_DATA).order;
  public readonly displayedColumns = Object.values(OrdersHistoryEnum);
  public readonly historyOrderKey = OrdersHistoryEnum;
  public readonly columnsConfig = ORDERS_HISTORY_COLUMNS_CONFIG;
  public readonly currentStatus = computed(() => this.orderItem.orderStatus);
  public readonly availableStatuses = signal<AvailableStatus[]>([]);
  public readonly ordersHistory = signal<OrderHistoryItem[]>([]);
  public readonly isOrderHistoryLoading = signal(false);

  public readonly isStatusEditable = computed(() => {
    const current = this.currentStatus();
    return current !== OrderStatus.DONE && current !== OrderStatus.CANCELLED;
  });

  public readonly IconName = IconName;
  public editStatusForm!: FormGroup;

  public ngOnInit(): void {
    this.loadOrderHistory();
    this.updateAvailableStatuses();

    this.editStatusForm = this.fb.group({
      statusId: [
        {
          value: this.availableStatuses().length > 0 ? '' : null,
          disabled: !this.isStatusEditable(),
        },
        Validators.required,
      ],
      comment: ['', [commentValidator()]],
    });

    this.editStatusForm
      .get('statusId')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((newStatus: OrderStatus) => {
        this.updateCommentValidation(newStatus);
      });
  }

  public onSave(): void {
    const formValue = this.editStatusForm.getRawValue();
    let apiStatus: OrderStatusApiValue;

    if (this.editStatusForm.invalid) {
      this.editStatusForm.markAllAsTouched();
      return;
    }

    switch (formValue.statusId) {
      case OrderStatus.NEW:
        apiStatus = OrderStatusApiValue.New;
        break;
      case OrderStatus.CANCELLED:
        apiStatus = OrderStatusApiValue.Cancelled;
        break;
      case OrderStatus.PROCESSING:
        apiStatus = OrderStatusApiValue.Processing;
        break;
      case OrderStatus.IN_PROGRESS:
        apiStatus = OrderStatusApiValue.InProgress;
        break;
      case OrderStatus.DONE:
        apiStatus = OrderStatusApiValue.Done;
        break;
      default:
        apiStatus = OrderStatusApiValue.New;
    }

    const orderStatusData = {
      OrderId: this.orderItem.id,
      Status: apiStatus,
      Comment: formValue.comment,
    };

    this.ordersService
      .updateOrderStatus(orderStatusData)
      .pipe(
        tap(() => {
          this.dialogRef.close({ success: true });
        }),
        catchError(() => {
          this.dialogRef.close({ success: false });

          this.handleOpenErrorModal();
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  public updateAvailableStatuses(): void {
    const current = this.currentStatus();
    let statuses: OrderStatus[] = [];

    switch (current) {
      case OrderStatus.NEW:
        statuses = [OrderStatus.PROCESSING, OrderStatus.CANCELLED];
        break;

      case OrderStatus.PROCESSING:
        statuses = [OrderStatus.IN_PROGRESS, OrderStatus.CANCELLED];
        break;

      case OrderStatus.IN_PROGRESS:
        statuses = [OrderStatus.PROCESSING, OrderStatus.DONE, OrderStatus.CANCELLED];
        break;

      case OrderStatus.DONE:
      case OrderStatus.CANCELLED:
        statuses = [];
        break;
    }

    this.availableStatuses.set(
      statuses.map((status) => ({
        id: status,
        name: status,
      })),
    );
  }

  public updateCommentValidation(newStatus: OrderStatus): void {
    const commentControl = this.editStatusForm.get('comment');

    const mandatoryStatuses = [OrderStatus.PROCESSING, OrderStatus.DONE, OrderStatus.CANCELLED];

    if (mandatoryStatuses.includes(newStatus)) {
      commentControl?.setValidators([Validators.required, commentValidator()]);
    } else {
      commentControl?.clearValidators();
    }
    commentControl?.updateValueAndValidity();
  }

  public formatOrderStatus(status: string): string {
    return status.replace(/([a-z])([A-Z])/g, '$1 $2');
  }

  public onCancel(): void {
    const confirmRef = this.dialog.open(ConfirmModalComponent, {
      ...CONFIRM_DIALOG_OPTIONS,
      panelClass: 'confirm-dialog',
    });

    this.dialogRef.addPanelClass('hidden-dialog');

    confirmRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.dialogRef.close({ success: false });
        } else {
          this.dialogRef.removePanelClass('hidden-dialog');
        }
      });
  }

  private loadOrderHistory(): void {
    this.isOrderHistoryLoading.set(true);

    this.ordersService
      .getOrderHistory(String(this.orderItem.customerId))
      .pipe(
        finalize(() => this.isOrderHistoryLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response) => {
          this.ordersHistory.set(response.data);
        },
        error: () => {
          this.handleOpenErrorModal();
        },
      });
  }

  private handleOpenErrorModal(): void {
    this.dialog.open(ErrorModalComponent, {
      data: {
        success: false,
        title: 'orders.table.errors.title',
        description: 'orders.table.errors.ordersLoadFail',
      },
    });
  }
}
